import { pubUrl, searchUrl } from 'findem-helpers';
import { COUNTRIES_LIST } from '../../../constants/countries';
import { DNI_MACROS, DEFAULT_FMAGIC, MACROS_GROUP_BY_CAT, FULLY_ROUNDED, RIGHT_ROUNDED, LEFT_ROUNDED } from './constant';

const monthNames = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];
const weekNames = ['1st Week', '2nd Week', '3rd Week', '4th Week', '5th Week'];

const CURRENT_COMPANY_SECTIONS = [
  'all_employee_opts',
  'new_employee_opts',
  'exited_employee_opts'
];

export const RANGE_MIN = 0;
export const RANGE_MAX = 360;
export const RANGE_STEP = 3;
export const RANGE_YEAR = 12;
export const RANGE_SPLITTER = '-';

export const fetchOwnerInfo = (joid: string) => {
  const content = { id: joid };
  const _key = 'owner_info-' + joid;
  // @ts-ignore
  if (window._reports_cache[_key]) {
    // @ts-ignore
    return window._reports_cache[_key];
  }
  return fetch(`${searchUrl}/pub/api/user_info`, {
    method: 'post',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(content)
  }).then((response) => {
    // @ts-ignore
    window._reports_cache[_key] = response.json();
    // @ts-ignore
    return window._reports_cache[_key];
  });
};

// Common payload org...
export const buildMultiMatchesPayloadByIcp = async (icp: any, joid: string) => {
  const { cxt_data } = icp;
  const payload = {
    keywords_mh: cxt_data.keywords_mh || [],
    location: icp.candidate_locations
      ? JSON.stringify(icp.candidate_locations)
      : '[]',
    macroids: cxt_data.default_filters || [],
    new_service: true
  };
  if (icp.cxt_data.ss_cxt?.sl_options?.exclude_company) {
    const user_state = await fetchOwnerInfo(joid);
    const domain = user_state.email.split('@')[1];
    if (
      !domain.match('[a-zA-Z0-9-.]*[0-9]+.[a-zA-Z]') &&
      !payload.keywords_mh.includes('workexp.company.domain: !' + domain)
    ) {
      payload.keywords_mh.push('workexp.company.domain: !' + domain);
    }
  }
  return payload;
};

export const hasCurrentCompanyCondition = (
  requirements: any
): boolean => {
  const has_current_company_qr: any = requirements.find(
    (r: any) => CURRENT_COMPANY_SECTIONS.indexOf(r.section_id) >= 0
  );
  return !!has_current_company_qr;
};

export const getDomainExcludeConditions = (
  user_state: any,
  search: any,
  reqs: any = [],
  domainToExcludeSearch?: string
)=> {
  if (!search){
    return [];
  }
  const conditions: string[] = [];
  if (domainToExcludeSearch && domainToExcludeSearch.length > 0) {
    // domainToExcludeSearch split by comma and add to requirements
    // check for exclude_company and exclude_prev_company flags
    const domains = domainToExcludeSearch.split(',');
    domains.forEach((domain) => {
      if (
        !hasCurrentCompanyCondition(reqs) &&
        search?.cxt_data?.ss_cxt?.sl_options?.exclude_company
      ) {
        conditions.push(`${'workexp.current.company.domain: !' + domain.trim().toLocaleLowerCase()}`);
      }
      if (
        !hasCurrentCompanyCondition(reqs) &&
        search?.cxt_data?.ss_cxt?.sl_options?.exclude_prev_company
      ) {
        conditions.push(`${'workexp.past.company.domain: !' + domain.trim().toLocaleLowerCase()}`);
      }
    });
  } else {
    if (
      !hasCurrentCompanyCondition(reqs) &&
      user_state &&
      user_state.email &&
      search?.cxt_data?.ss_cxt?.sl_options?.exclude_company
    ) {
      const domain = user_state.email.split('@')[1];
      if (!domain.match('[a-zA-Z0-9-.]*[0-9]+.[a-zA-Z]')) {
        conditions.push(`${'workexp.current.company.domain: !' + domain.toLocaleLowerCase()}`);
      }
    }
    if (
      !hasCurrentCompanyCondition(reqs) &&
      user_state &&
      user_state.email &&
      search?.cxt_data?.ss_cxt?.sl_options?.exclude_prev_company
    ) {
      const domain = user_state.email.split('@')[1];
      if (!domain.match('[a-zA-Z0-9-.]*[0-9]+.[a-zA-Z]')) {
        conditions.push(`${'workexp.past.company.domain: !' + domain.toLocaleLowerCase()}`);
      }
    }
  }
  return conditions;
}

export const buildMultiMatchesV2PayloadByIcp = async (
  icp: any,
  joid: string,
  domainToExcludeSearch?: string
) => {
  const payload: any = {
    requirements: icp.requirements || []
  };
  const user_state = await fetchOwnerInfo(joid);
  const _requirements: string[] = getDomainExcludeConditions(user_state, icp, icp.requirements, domainToExcludeSearch);
  if (_requirements.length > 0) {
    payload.requirements.push({
      bool_str: `(${_requirements.join(' AND ')})`,
      section_id: 'exclude_domain',
      section_type: 'Must Have'
    });
  }
  return payload;
};

export const buildMatchesAnalyzePayloadByIcp = async (
  icp: any,
  joid: string,
  count: number = 10000,
  prids: string[] = [],
  domainToExcludeSearch?: string
) => {
  const _magic_attr = Object.keys(DEFAULT_FMAGIC);
  const _funding_stage_attr = Object.keys(
    MACROS_GROUP_BY_CAT.curr_funding_stage
  );
  const _degree_attr = Object.keys(MACROS_GROUP_BY_CAT.matches_by_degree);
  const payload: any =
    icp?.mode === 'v2'
      ? await buildMultiMatchesV2PayloadByIcp(icp, joid, domainToExcludeSearch)
      : await buildMultiMatchesPayloadByIcp(icp, joid);
  payload['joid'] = joid;
  payload['count'] = count;
  payload['v2_fields'] = true;
  payload['detail'] = true;
  payload['prids'] = prids;
  payload['opids'] = [..._magic_attr, ..._funding_stage_attr, ..._degree_attr];
  payload.requirements.push({
    bool_str: `(${_magic_attr.join(' OR ')})`,
          section_type: 'Nice to Have',
          section_name: 'Internal',
          section_id: 'internal_fmmagic_nice_to_haves'
  });
  payload.requirements.push({
    bool_str: `(${_funding_stage_attr.join(' OR ')})`,
          section_type: 'Nice to Have',
          section_name: 'Internal',
          section_id: 'internal_funding_stage_attr_nice_to_haves'
  });
  payload.requirements.push({
    bool_str: `(${_degree_attr.join(' OR ')})`,
          section_type: 'Nice to Have',
          section_name: 'Internal',
          section_id: 'internal_degree_attr_nice_to_haves'
  });
  return payload;
};

export const setDynamicAnalyzeData = (data: any) => {
  const { analysis_info } = data;
  analysis_info['magic_map'] = {};
  analysis_info['funding_map'] = {};
  analysis_info['degree_map'] = {};
  analysis_info['major_map'] = {};

  // Ensure optionals_id_map exists
  if (!analysis_info['optionals_id_map']) {
    analysis_info['optionals_id_map'] = {};
  }

  Object.keys(DEFAULT_FMAGIC).forEach((macro_id: string) => {
    if (analysis_info['optionals_id_map'][macro_id]) {
      analysis_info['magic_map'][macro_id] =
        analysis_info['optionals_id_map'][macro_id];
      delete analysis_info['optionals_id_map'][macro_id];
    }
  });
  Object.keys(MACROS_GROUP_BY_CAT.curr_funding_stage).forEach(
    (macro_id: string) => {
      if (analysis_info['optionals_id_map'][macro_id]) {
        analysis_info['funding_map'][macro_id] =
          analysis_info['optionals_id_map'][macro_id] || [];
        delete analysis_info['optionals_id_map'][macro_id];
      }
    }
  );
  Object.keys(MACROS_GROUP_BY_CAT.matches_by_degree).forEach(
    (macro_id: string) => {
      if (analysis_info['optionals_id_map'][macro_id]) {
        analysis_info['degree_map'][macro_id] =
          analysis_info['optionals_id_map'][macro_id] || [];
        delete analysis_info['optionals_id_map'][macro_id];
      }
    }
  );
  Object.keys(MACROS_GROUP_BY_CAT.matches_by_major).forEach(
    (macro_id: string) => {
      if (analysis_info['optionals_id_map'][macro_id]) {
        analysis_info['major_map'][macro_id] =
          analysis_info['optionals_id_map'][macro_id] || [];
        delete analysis_info['optionals_id_map'][macro_id];
      }
    }
  );
  return data;
};

export const buildMatchesPayloadByIcp = (icp: any) => {
  const negs: any = {};
  const opids: string[] = [];
  icp?.requirements?.forEach((macro: any) => {
    negs[`${macro.macro_id}`] = macro.is_negative;
    opids.push(macro.macro_id);
  });
  const { cxt_data } = icp;
  const payload = {
    detail: true,
    include_stale: true,
    keywords_mh: cxt_data.keywords_mh || [],
    keywords_opt: cxt_data.keywords_opt || [],
    location: icp.candidate_locations
      ? JSON.stringify(icp.candidate_locations)
      : '[]',
    macroids: cxt_data.default_filters || [],
    negs,
    new_service: true,
    opids,
    start_index: 0
  };
  return payload;
};

export const buildMatchesV2PayloadByIcp = (icp: any) => {
  const opids: string[] = [];
  const payload = {
    detail: true,
    include_stale: true,
    requirements: icp.requirements || [],
    new_service: true,
    opids,
    start_index: 0
  };
  return payload;
};

export const buildMultiMatchesPayloadForCountry = async (
  icp: any,
  for_country: string = 'United States',
  macroids: any = [],
  setToMatch: string[] = [],
  joid: string
) => {
  const payload: any = await buildMultiMatchesPayloadByIcp(icp, joid);
  if (for_country === 'Global') {
    payload['target_country_multi'] = COUNTRIES_LIST;
  } else {
    payload['target_country'] = for_country;
  }

  if (setToMatch.length > 0) {
    payload['to_match'] = setToMatch;
  }

  if (macroids) {
    payload.macroids = [...payload.macroids, ...macroids];
  }
  return payload;
};

export const buildMultiMatchesV2PayloadForCountry = async (
  icp: any,
  for_country: string = 'United States',
  macroids: any = [],
  setToMatch: string[] = [],
  joid: string,
  domainToExcludeSearch?: string
) => {
  const payload: any = await buildMultiMatchesV2PayloadByIcp(icp, joid, domainToExcludeSearch);
  if (for_country === 'Global') {
    payload['target_country_multi'] = COUNTRIES_LIST;
  } else {
    payload['target_country'] = for_country;
  }

  if (setToMatch.length > 0) {
    payload['to_match'] = setToMatch;
  }
  return payload;
};

// Overview Data Transform
export const buildOverviewData = (data: any) => {
  // @ts-ignore
  const _dni = Object.keys(DNI_MACROS);
  const _magic = Object.keys(DEFAULT_FMAGIC);
  const { target_locations, count_by_queries } = data;

  // locations sort by count *new array because it will effect maps tool tips.
  const _sorted_target_locations = target_locations
    ? [...target_locations].sort((contA: any, contB: any) => {
        return contB.profile_count - contA.profile_count;
      })
    : [];

  const _diversity_n_magic: any = { dni: {}, magic: {} };
  // dni grouping.
  [..._dni, ..._magic].forEach((macro) => {
    if (_dni.includes(macro) && count_by_queries[macro] > 0) {
      _diversity_n_magic.dni[macro] = count_by_queries[macro];
    } else if (count_by_queries[macro] > 0) {
      _diversity_n_magic.magic[macro] = count_by_queries[macro];
    }
  });
  return {
    _diversity_n_magic,
    _countries: _sorted_target_locations
  };
};

export const isUSICP = (icp: any) => {
  const { candidate_locations } = icp;
  return candidate_locations.length > 0
    ? candidate_locations.every((loc: any) => loc.country == 'United States')
    : false;
};


const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1)


const handleLangeuage: any = (languages: any) => {
  const _languages: any = {};
  languages.forEach((language: any) => {
    const _lang_tokens: string[] = language.name.split(':');
    if(_lang_tokens.length === 2){
      const _lang = _lang_tokens[0].trim();
      let _prof = capitalize(_lang_tokens[1].trim().split('_').join(' ').toLowerCase());
      if(_prof === ''){
        _prof = 'Unclassified';
      }
      _prof = _prof.replace(' proficiency', '');
      if(_languages[_lang]){
        _languages[_lang].prfs = [..._languages[_lang].prfs, ...language.prfs];
      }else{
        _languages[_lang] = {
          name:_lang,
          prfs: [...language.prfs]
        };
      }
      if(_languages[_lang][_prof]){
        _languages[_lang][_prof] += language.prfs.length;
      }else{
        _languages[_lang][_prof] = language.prfs.length;
      }
    }
  });
  if(Object.values(_languages).length > 0){
    // sort calues by prfs length
    return Object.values(_languages).sort((a: any, b:any) => b.prfs.length - a.prfs.length);
  }else{
    return []
  }
}

// Key Insights Data Transform..

export const buildInsightsData = (matches: any) => {
  const {
    university_map,
    title_map,
    curr_comp_map,
    companies,
    experience_map
  } = matches.analysis_info;
  const {
    skills_map,
    timezone_map,
    location_raw_map,
    magic_map,
    funding_map,
    degree_map,
    major_map,
    language_map
  } = matches.analysis_info;
  const _sliceBy = 10;
  // SCHOOLS
  const univs: any[] = [];
  Object.keys(university_map).forEach((c) => {
    const prfs = university_map[c];
    univs.push({ name: c, prfs });
  });
  univs.sort((a, b) => {
    return b.prfs.length - a.prfs.length;
  });

  // Current TITLE
  const c_titles: any[] = [];
  Object.keys(title_map).forEach((c) => {
    const prfs = title_map[c];
    c_titles.push({ name: c, prfs });
  });
  c_titles.sort((a, b) => {
    return b.prfs.length - a.prfs.length;
  });

  // Skills
  const c_skills: any[] = [];
  Object.keys(skills_map).forEach((c) => {
    const prfs = skills_map[c];
    c_skills.push({ name: c, prfs });
  });
  c_skills.sort((a, b) => {
    return b.prfs.length - a.prfs.length;
  });

  // Timezone
  const c_timezones: any[] = [];
  Object.keys(timezone_map).forEach((c) => {
    const prfs = timezone_map[c];
    c_timezones.push({ name: c, prfs });
  });
  c_timezones.sort((a, b) => {
    return b.prfs.length - a.prfs.length;
  });

  // locations
  const c_locations: any[] = [];
  Object.keys(location_raw_map).forEach((c) => {
    const prfs = location_raw_map[c] || [];
    c_locations.push({ name: c, prfs });
  });
  c_locations.sort((a, b) => {
    return b.prfs.length - a.prfs.length;
  });

  // companies
  const all_curr_companies: any[] = [];
  Object.keys(curr_comp_map).forEach((c) => {
    const prfs = curr_comp_map[c] || [];
    all_curr_companies.push({ name: companies[c].name, prfs });
  });
  all_curr_companies.sort((a, b) => {
    return b.prfs.length - a.prfs.length;
  });

  // magic
  const c_magic: any[] = [];
  Object.keys(magic_map).forEach((c) => {
    const prfs = magic_map[c] || [];
    if (prfs.length > 0) {
      c_magic.push({ name: DEFAULT_FMAGIC[c], prfs });
    }
  });
  c_magic.sort((a, b) => {
    return b.prfs.length - a.prfs.length;
  });

  // funding_map
  const c_funding: any[] = [];
  Object.keys(funding_map).forEach((c) => {
    const prfs = funding_map[c];
    c_funding.push({ name: MACROS_GROUP_BY_CAT.curr_funding_stage[c], prfs });
  });
  c_funding.sort((a, b) => {
    return b.prfs.length - a.prfs.length;
  });

  // degree_map
  const c_degree: any[] = [];
  Object.keys(degree_map).forEach((c) => {
    const prfs = degree_map[c] || [];
    c_degree.push({ name: MACROS_GROUP_BY_CAT.matches_by_degree[c], prfs });
  });
  c_degree.sort((a, b) => {
    return b.prfs.length - a.prfs.length;
  });

  // major_map
  const c_major: any[] = [];
  Object.keys(major_map).forEach((c) => {
    const prfs = major_map[c] || [];
    c_major.push({ name: MACROS_GROUP_BY_CAT.matches_by_major[c], prfs });
  });
  c_major.sort((a, b) => {
    return b.prfs.length - a.prfs.length;
  });

  const exp_levels: any[] = [];
  Object.keys(experience_map).forEach((c) => {
    const prfs = experience_map[c] || [];

    exp_levels.push({ name: c, prfs });
  });

  exp_levels.sort((a, b) => {
    return b.prfs.length - a.prfs.length;
  });

  // language
  const c_language: any[] = [];
  Object.keys(language_map).forEach((c) => {
    const prfs = language_map[c] || [];
    c_language.push({ name: c, prfs });
  });
  c_language.sort((a, b) => b.prfs.length - a.prfs.length);
  const _c_language: any[] = handleLangeuage(c_language);
  return {
    top_skills: c_skills.slice(0, _sliceBy),
    top_curr_comps: all_curr_companies.slice(0, _sliceBy),
    top_schools: univs.slice(0, _sliceBy),
    top_exp: exp_levels.slice(0, _sliceBy),
    top_timezone: c_timezones.slice(0, _sliceBy),
    top_job_titles: c_titles.slice(0, _sliceBy),
    top_locations: c_locations.slice(0, _sliceBy),
    top_funding: c_funding.slice(0, _sliceBy),
    top_magic: c_magic.slice(0, _sliceBy),
    top_degree: c_degree.slice(0, _sliceBy),
    top_major: c_major.slice(0, _sliceBy),
    top_language: _c_language.slice(0, _sliceBy)
  };
};

export const getMetricsByGender = (data: any, matches_analyze: any) => {
  const { Female, Male, female, male } = matches_analyze.gender_map;
  let male_count = 0;
  let female_count = 0;
  let unknown = 0;
  data.forEach((prf: string) => {
    if (Female?.includes(prf) || female?.includes(prf)) {
      female_count++;
    } else if (Male?.includes(prf) || male?.includes(prf)) {
      male_count++;
    } else {
      unknown++;
    }
  });
  return {
    'cmetrics-male': male_count,
    'cmetrics-female': female_count,
    unknown
  };
};

export const getMaxOfExperienceOrComps = (
  prids: any,
  matches_analyze: any,
  data_for: string
) => {
  const keyArray: any = [];
  const keyObject: any = {};
  const data_map = matches_analyze[data_for] || {};
  Object.keys(data_map).forEach((key) => {
    let string = data_map[key].join('##');
    string = `${key}##${string}`;
    keyArray.push(string);
  });

  prids.forEach((prid: any) => {
    const experienceString = keyArray.find((exp: any) => exp.includes(prid));

    const keyObjectvalue = experienceString?.split('##')[0];
    if (keyObjectvalue) {
      if (keyObject[keyObjectvalue]) {
        keyObject[keyObjectvalue] = keyObject[keyObjectvalue] + 1;
      } else {
        keyObject[keyObjectvalue] = 1;
      }
    }
  });
  const _max = Object.keys(keyObject).sort(function(a, b) {
    return keyObject[b] - keyObject[a];
  })[0] || '-';
  return data_for === 'experience_map'
    ? `${parseInt(_max) === 1 ? `${_max} year` : `${_max} years`}`
    : `${matches_analyze.companies[_max]?.name || '-'}`;
};

export const getProfileLocation = (prf: any) => {
  let loc_str = '';
  let loc: any = {};
  try {
    loc = prf.candidate.location;
    loc_str =
      (loc.city ? loc.city : '') +
      (loc.city && (loc.state || loc.country) ? ', ' : '') +
      (loc.state ? loc.state : '') +
      (loc.state && loc.country ? ', ' : '') +
      (loc.country ? loc.country : '');
  } catch (e) {
    console.log('error', e);
  }
  return loc_str;
};

const _parentheses_rg = new RegExp(/\(.*?\)/g);

const BooleanStrTokensByParanthesis = (str: string | undefined) => {
  if (!str || str.length === 0) return [];

  if (str.indexOf('(') === -1) return [str];

  const tokens: string[] = [];
  for (const match of str.match(_parentheses_rg) || []) {
    const _no_bracket_match = match.replace(/[()]/g, '');
    tokens.push(_no_bracket_match);
  }
  return tokens;
};

export const buildSummaryMacroInfoV2 = (icp: any, macros_by_id_: any) => {
  const { requirements } = icp;
  const mustHaveStrs: string[] = [];
  const niceToHaveStrs: string[] = [];
  const locStr: string[] = [];
  const NOT_IDENTIFIER = '!';
  // filter section_id === internal
  const sanatizedRequirements = requirements.filter(
    (requirement: any) => requirement.section_id.indexOf('internal') === -1
  );
  sanatizedRequirements.forEach((requirement: any) => {
    const { bool_str, section_id, section_type, section_name } = requirement;
    //TODO:- handle roles/must have/ advanced cases spl...
    const _attrs_str = [];
    if (section_id === 'internal' || section_id === 'exclude_domain') {
      return;
    }
    if (section_id === 'location') {
      const _attrs = JSON.parse(bool_str);
      _attrs.forEach((attr: any) => {
        const address = attr.negate_match
          ? `NOT ${attr.address}`
          : attr.address;
        _attrs_str.push(address);
      });
      locStr.push(
        `<span class="cat-pill">${section_name}</span> ${_attrs_str.join(
          ' <span style="color:#808080;padding: 0px 3px;">or</span> '
        )}`
      );
      return;
    }else if(requirement.req_cxt?.bool_cxt) {
      (section_type === 'Must Have' ? mustHaveStrs : niceToHaveStrs).push(
        `<span class="cat-pill">${section_name}</span> (${requirement.req_cxt.bool_cxt.join(
          `<span style="color:#808080;padding: 0px 3px;">or</span>`
        )})`
      );
    } else {
      const _rules: string[] = BooleanStrTokensByParanthesis(
        unescape(bool_str)
      );
      _rules.forEach(async (rule: string) => {
        const operator = rule.indexOf(' AND ') > -1 ? ' AND ' : ' OR ';
        const _attrs = rule.split(operator);
        const company_ids: string[] = [];
        const companyIdsMap: any = {};
        _attrs.forEach((attr: any) => {
          if (attr.includes('.company.id:')) {
            company_ids.push(attr.split(':').at(-1));
          }
        });
        if (company_ids.length > 0) {
          const companyDetailsById = await fetch(`${pubUrl}/pub/api/company_details?meta_refs=${company_ids.join(',')}`);
          const companyDetails = await companyDetailsById.json();
          companyDetails?.data?.companyListDetails.forEach((company: any) => {
            companyIdsMap[company.meta_ref] = company.company_name;
          });
        }
        const _attrs_str = [];
        _attrs.forEach((attr: any) => {
          let _attr = attr.split(':').splice(-1)[0];
          const is_neg = _attr.startsWith(NOT_IDENTIFIER);
          _attr = _attr.replace(NOT_IDENTIFIER, '');
          if (DNI_MACROS[_attr]) {
            const _name = is_neg ? `NOT ${DNI_MACROS[_attr]}` : `${DNI_MACROS[_attr]}`;
            _attrs_str.push(_name);
          } else if (macros_by_id_[_attr]) {
            const macro = macros_by_id_[_attr];
            let _name = is_neg ? `NOT ${macro.name}` : `${macro.name}`;
            if (
              macro &&
              macro.auto_gen_cxt &&
              !macro.auto_gen_cxt.declared &&
              macro.auto_gen_cxt.verified
            ) {
              _name += '<sup>*</sup>';
            }
            _attrs_str.push(_name);
            // company ids.
          } else if(companyIdsMap[_attr]){
            const name = companyIdsMap[_attr];
            let _name = is_neg ? `NOT ${name}` : `${name}`;
            _attrs_str.push(_name);
          } else {
            if(section_id && ['workexp', 'curr_experience'].includes(section_id)) {
              const range: string[] = _attr.split('-');
              if(range.length === 2 && _attr.includes('-')) {
                if(!range[1]){
                  if(parseInt(range[0]) < RANGE_YEAR){
                    _attr = `${range[0]}+ months`;
                  }else{
                    _attr = `${Math.floor(parseInt(range[0])/RANGE_YEAR)}+ years`;
                  }
                }else{
                  if(parseInt(range[0]) < RANGE_YEAR && parseInt(range[1]) < RANGE_YEAR){
                    _attr = `${range[0]}-${range[1]} months`;
                  } else if(parseInt(range[0]) < RANGE_YEAR && parseInt(range[1]) >= RANGE_YEAR){
                    _attr = `${range[0]} months - ${Math.floor(parseInt(range[1])/RANGE_YEAR)} years`;
                  } else{
                    _attr = `${Math.floor(parseInt(range[0])/RANGE_YEAR)} - ${Math.floor(parseInt(range[1])/RANGE_YEAR)} years`;
                  }
                }
              }
            }
            _attrs_str.push(is_neg ? `NOT ${_attr}` : `${_attr}`);
          }
        });
        if (section_type === 'Must Have') {
          mustHaveStrs.push(
            `<span class="cat-pill">${section_name}</span> (${_attrs_str.join(
              `<span style="color:#808080;padding: 0px 3px;">${operator}</span>`
            )})`
          );
        } else {
          niceToHaveStrs.push(
            `<span class="cat-pill">${section_name}</span> ${_attrs_str.join(
              `<span style="color:#808080;padding: 0px 3px;">${operator}</span>`
            )}`
          );
        }
      });
    }
  });
  return {
    mustHaveStrs,
    niceToHaveStrs,
    locStr
  };
};

export const buildSummaryMacroInfo = (icp: any, macros_by_id_: any) => {
  const mustHaveStrs: string[] = [];
  const niceToHaveStrs: string[] = [];
  const locStr: string[] = [];
  const { requirements, cxt_data, candidate_locations } = icp;
  const { default_filters, keywords_other, keywords_opt } = cxt_data;
  const is_icp_v2 = icp?.mode === 'v2' ? true : false;

  // location
  candidate_locations.forEach((loc: any) => {
    locStr.push(loc.negate_match ? `Not in ${loc.address}` : loc.address);
  });

  // musthaves
  default_filters.forEach((mid: string) => {
    if (mid.includes('auto_mm_') && macros_by_id_[mid]) {
      const _macro: any = macros_by_id_[mid];
      const _comp_macros: string[] = _autoMacrosbyNamesArr(
        mid,
        [],
        false,
        macros_by_id_
      ) as string[];
      if (
        (_macro.defs.length > 0 && _macro.defs[0]?.binary_op == 'or') ||
        (_macro.keyword_cxt.length > 0 &&
          (_macro.keyword_cxt[0]?.binary_op == 'or' ||
            _macro.keyword_cxt[0]?.next_op == 'or'))
      ) {
        mustHaveStrs.push(
          `<span class="cat-pill">${getMacroCategoryName(
            macros_by_id_,
            mid
          )}</span> ${_comp_macros.join(
            ' <span style="color:#808080;padding: 0px 3px;">or</span> '
          )}`
        );
      } else {
        mustHaveStrs.push(
          `<span class="cat-pill">${getMacroCategoryName(
            macros_by_id_,
            mid
          )}</span> ${_comp_macros.join(
            ' <span style="color:#808080;padding: 0px 3px;">and</span> '
          )}`
        );
      }
    } else if (macros_by_id_[mid]) {
      if (
        macros_by_id_[mid].name &&
        !mustHaveStrs.includes(macros_by_id_[mid].name)
      ) {
        let _name = macros_by_id_[mid].name;
        const macro = macros_by_id_[mid];
        if (
          macro &&
          macro.auto_gen_cxt &&
          !macro.auto_gen_cxt.declared &&
          macro.auto_gen_cxt.verified
        ) {
          _name += '<sup>*</sup>';
        }
        mustHaveStrs.push(
          unescape(
            `<span class="cat-pill">${getMacroCategoryName(
              macros_by_id_,
              mid
            )}</span> ${_name}`
          )
        );
      }
    }
  });

  if (!is_icp_v2) {
    // nicetohaves
    requirements.forEach((requirement: any) => {
      if (
        requirement.req_type == 'good to have' &&
        !requirement.macro_id.includes('auto_mm_') &&
        macros_by_id_[requirement.macro_id]
      ) {
        let _name = macros_by_id_[requirement.macro_id].name;
        _name =
          '<span class="cat-pill">' +
          getMacroCategoryName(macros_by_id_, requirement.macro_id) +
          '</span> ' +
          (requirement.is_negative ? `Not ${_name}` : _name);
        niceToHaveStrs.push(unescape(_name));
      }
    });

    requirements.forEach((requirement: any) => {
      if (
        requirement.req_type == 'must have' &&
        !requirement.macro_id.includes('auto_mm_') &&
        macros_by_id_[requirement.macro_id]
      ) {
        let _name = macros_by_id_[requirement.macro_id].name;
        _name =
          '<span class="cat-pill">' +
          getMacroCategoryName(macros_by_id_, requirement.macro_id) +
          '</span> ' +
          (requirement.is_negative ? `Not ${_name}` : _name);
        mustHaveStrs.push(unescape(_name));
      }
    });
  }

  // other keywords to nice to have...
  keywords_other.forEach((keyword: string) => {
    const _tokens = keyword.split(':');
    if (_tokens.length == 2) {
      niceToHaveStrs.push(
        unescape(
          `<span class="cat-pill">${getKeywordCategoryName(keyword)}</span> ${
            _tokens[1]
          }`
        )
      );
    }
  });

  // other keywords to nice to have skills...
  keywords_opt.forEach((keyword: string) => {
    niceToHaveStrs.push(
      unescape(
        `<span class="cat-pill">Skills</span> ${
          keyword
        }`
      )
    );
  });

  return {
    mustHaveStrs,
    niceToHaveStrs,
    locStr
  };
};

const _autoMacrosbyNamesArr = (
  macro_id: string,
  _arr: string[] = [],
  _inverse: boolean = false,
  macros_by_id_: any
) => {
  const { defs, keyword_cxt, name } = macros_by_id_[macro_id];
  if (
    defs.length == 0 &&
    keyword_cxt.length == 0 &&
    !name?.includes('Internal_Compound') &&
    !_arr.includes(name)
  ) {
    return _arr.push(unescape(_inverse ? `Not ${name}` : name));
  }
  // check for defs.
  if (defs.length >= 0) {
    defs.forEach((def: any) => {
      if (def.macro_id.includes('auto_mm_') && macros_by_id_[def.macro_id]) {
        _autoMacrosbyNamesArr(def.macro_id, _arr, def.inverse, macros_by_id_);
      } else if (macros_by_id_[def.macro_id]) {
        if (
          macros_by_id_[def.macro_id]?.name &&
          !_arr.includes(macros_by_id_[def.macro_id]?.name)
        ) {
          let _name = macros_by_id_[def.macro_id].name;
          const macro = macros_by_id_[def.macro_id];
          if (
            macro &&
            macro.auto_gen_cxt &&
            !macro.auto_gen_cxt.declared &&
            macro.auto_gen_cxt.verified
          ) {
            _name += '<sup>*</sup>';
          }
          _arr.push(unescape(def.inverse ? `Not ${_name}` : _name));
        }
      }
    });
  }

  // check for keyword_cxt
  if (keyword_cxt.length >= 0) {
    keyword_cxt.forEach((keyword: any) => {
      const _terms = keyword.search_term?.split(':');
      if (_terms.length == 2) {
        _arr.push(
          unescape(
            _inverse || unescape(_terms[1]).indexOf('!') == 0
              ? `Not ${unescape(_terms[1]).replace('!', '')}`
              : _terms[1]
          )
        );
      } else {
        _arr.push(
          unescape(
            _inverse || unescape(_terms).indexOf('!') == 0
              ? `Not ${unescape(_terms).replace('!', '')}`
              : _terms
          )
        );
      }
    });
  }
  return _arr;
};

export const deriveCampaignData = (camp: any) => {
  const total: any = {
    contacted: 0,
    recipient: 0,
    opened: 0,
    clicked: 0,
    replied: 0,
    interested: 0
  };
  const monthly: any = [];
  const weekly: any = [];
  const { all_emails_phase_stats_map } = camp;

  const toDate = (date_str: string) => {
    const parts: any = date_str.split('/');
    return new Date(parts[2], parts[1] - 1, parts[0]);
  };

  const toMonth = (date_str: string) => {
    if (!date_str) {
      return date_str;
    }
    const _date = toDate(date_str);
    const _month = monthNames[_date.getMonth()];
    return `${_month} ‘${_date
      .getFullYear()
      .toString()
      .substring(2)}`;
  };

  const getWeek = (date_str: string) => {
    if (!date_str) {
      return date_str;
    }
    const d = toDate(date_str);
    const date = d.getDate();
    const day = d.getDay();
    return weekNames[Math.ceil((date - 1 - day) / 7)];
  };

  let _month_temp: any = {
    name: '',
    contacted: 0,
    recipient: 0,
    opened: 0,
    clicked: 0,
    replied: 0,
    interested: 0
  };
  let _weekly_temp: any = {
    name: '',
    contacted: 0,
    recipient: 0,
    opened: 0,
    clicked: 0,
    replied: 0,
    interested: 0
  };
  const _sorted_dates = Object.keys(all_emails_phase_stats_map).sort(
    (date1: string, date2: string) =>
      toDate(date2).getTime() - toDate(date1).getTime()
  );
  _sorted_dates.forEach((date: string, idx: number) => {
    const { intro_count, reply_count } = all_emails_phase_stats_map[date];
    // total count
    total.contacted += intro_count.contacted;
    total.recipient += intro_count.sent;
    total.opened += intro_count.opened;
    total.clicked += intro_count.clicked_link;
    total.replied += intro_count.replied;
    total.interested += reply_count.interested;

    // monthly count
    const _monthName = toMonth(date);
    _month_temp.name = _monthName;
    _month_temp.contacted += intro_count.contacted;
    _month_temp.recipient += intro_count.sent;
    _month_temp.opened += intro_count.opened;
    _month_temp.clicked += intro_count.clicked_link;
    _month_temp.replied += intro_count.replied;
    _month_temp.interested += reply_count.interested;
    if (_monthName !== toMonth(_sorted_dates[idx + 1])) {
      monthly.push(_month_temp);
      _month_temp = {
        name: '',
        contacted: 0,
        recipient: 0,
        opened: 0,
        clicked: 0,
        replied: 0,
        interested: 0
      };
    }

    // weekly count...
    const _weekName = `${getWeek(date)} of ${_monthName}`; // 2nd week of Nov ‘21
    _weekly_temp.name = _weekName;
    _weekly_temp.contacted += intro_count.contacted;
    _weekly_temp.recipient += intro_count.sent;
    _weekly_temp.opened += intro_count.opened;
    _weekly_temp.clicked += intro_count.clicked_link;
    _weekly_temp.replied += intro_count.replied;
    _weekly_temp.interested += reply_count.interested;
    if (
      _weekName !==
      `${getWeek(_sorted_dates[idx + 1])} of ${toMonth(_sorted_dates[idx + 1])}`
    ) {
      weekly.push(_weekly_temp);
      _weekly_temp = {
        name: '',
        contacted: 0,
        recipient: 0,
        opened: 0,
        clicked: 0,
        replied: 0,
        interested: 0
      };
    }
  });

  return {
    total,
    monthly,
    weekly
  };
};

// Nasty
const getMacroCategoryName = (macros: any, macro_id: any): string => {
  const macro: any = macros[macro_id];
  if (!macro) {
    return 'Others';
  }
  // case of automacro
  if (macro.ref.includes('auto_mm_')) {
    if (macro.name.indexOf('findem_timezone_') >= 0) {
      return 'Timezone';
    } else {
      const { defs } = macro;
      if (defs?.length > 0) {
        const def: any = defs[0];
        return getMacroCategoryName(macros, def.macro_id);
      } else {
        return getKeywordCategoryName(macro?.name || '');
      }
    }
  }

  // else others...
  if (
    macro?.ui_categories?.indexOf('Special') >= 0 ||
    macro?.ui_categories?.indexOf('Findem Magic') >= 0
  ) {
    return 'Findem magic';
  } else if (
    macro?.macro_taxonomy &&
    macro?.macro_taxonomy.length &&
    (macro?.macro_taxonomy[0].includes('Size') ||
      macro?.macro_taxonomy[0].includes('Business Model') ||
      macro?.macro_taxonomy[0].includes('Stage') ||
      macro?.macro_taxonomy[0].includes('Company Lists') ||
      macro?.macro_taxonomy[0].includes('Rate'))
  ) {
    let _sub = '';
    if (macro?.macro_taxonomy[0].includes('Size')) {
      _sub = ' Size';
    } else if (macro?.macro_taxonomy[0].includes('Business Model')) {
      _sub = ' Business Model';
    } else if (macro?.macro_taxonomy[0].includes('Stage')) {
      _sub = ' Stage';
    } else if (macro?.macro_taxonomy[0].includes('Rate')) {
      _sub = ' Growth Rate';
    }
    if (macro.macro_taxonomy[0].includes('Past or Current')) {
      return `Past or Current Company${_sub}`;
    } else if (macro.macro_taxonomy[0].includes('Past')) {
      return `Past Company${_sub}`;
    } else if (macro.macro_taxonomy[0].includes('Recent')) {
      return `Recent Company${_sub}`;
    } else if (macro.macro_taxonomy[0].includes('Current')) {
      return `Current Company${_sub}`;
    }
  } else if (
    macro?.macro_taxonomy &&
    macro?.macro_taxonomy.length &&
    macro?.macro_taxonomy[0].includes('Degree')
  ) {
    return 'Degree';
  } else if (
    macro?.macro_taxonomy &&
    macro?.macro_taxonomy.length &&
    macro?.macro_taxonomy[0].includes('Role')
  ) {
    if (macro.macro_taxonomy[0].includes('Past or Current')) {
      return 'Past or Current Role';
    } else if (macro.macro_taxonomy[0].includes('Past')) {
      return 'Past Role';
    } else if (macro.macro_taxonomy[0].includes('Recent')) {
      return 'Recent Role';
    } else if (macro.macro_taxonomy[0].includes('Current')) {
      return 'Current Role';
    }
  } else if (
    macro?.macro_taxonomy &&
    macro?.macro_taxonomy.length &&
    macro?.macro_taxonomy[0].includes('School')
  ) {
    return 'School';
  } else if (
    macro?.macro_taxonomy &&
    macro?.macro_taxonomy.length &&
    macro?.macro_taxonomy[0].includes('Major')
  ) {
    return 'Major';
  } else if (
    macro?.ui_categories?.includes('Special') ||
    macro?.ui_categories?.indexOf('Findem Magic') >= 0
  ) {
    return 'Findem magic';
  } else if (macro?.ui_categories?.includes('Programming Languages')) {
    return 'Skills';
  } else if (
    macro?.macro_taxonomy &&
    macro?.macro_taxonomy.length &&
    macro?.macro_taxonomy[0].includes('Work Experience')
  ) {
    if (macro.macro_taxonomy[0].includes('Past or Current')) {
      return 'Past or Current Experience';
    } else if (macro.macro_taxonomy[0].includes('Past')) {
      return 'Past Experience';
    } else if (macro.macro_taxonomy[0].includes('Recent')) {
      return 'Recent Experience';
    } else if (macro.macro_taxonomy[0].includes('Current')) {
      return 'Current Experience';
    } else if (macro.macro_taxonomy[0].includes('Overall')) {
      return 'Overall Experience';
    }
  } else if (macro?.ui_categories?.indexOf('Role') >= 0) {
    return 'Role';
  }

  return 'Others';
};

// 2: 'workexp.recent.company.name: zoro'
// 1: 'workexp.recent.company.name: Google'
// 0: 'workexp.current.company.product_categories: Help Desk Software'
// 3: 'workexp.recent.company.name: Wingify'
const getKeywordCategoryName = (keyword: string) => {
  if (
    keyword.includes('product_categories') ||
    keyword.includes('company.name')
  ) {
    let _sub = '';
    if (keyword.includes('product_categories')) {
      _sub = ' Product Categories';
    } else if (keyword.includes('company.name')) {
      _sub = ' Company Name';
    }
    if (keyword.includes('Past or Current')) {
      return 'Past or Current Experience';
    } else if (keyword.includes('.past.')) {
      return `Past${_sub}`;
    } else if (keyword.includes('.recent.')) {
      return `Recent${_sub}`;
    } else if (keyword.includes('.current.')) {
      return `Current${_sub}`;
    }
  } else if (keyword.includes('.title')) {
    if (keyword.includes('.past.')) {
      return 'Past Role';
    } else if (keyword.includes('.recent.')) {
      return 'Recent Role';
    } else if (keyword.includes('.current.')) {
      return 'Current Role';
    } else if (keyword.includes('workexp.title')) {
      return 'Past or Current Role';
    }
  }
  return 'Others';
};
export const dateCompareMostRecent = (a: any, b: any) => {
  const getTime = (date_string: string) => {
    let date: string | number = date_string;
    if (!date_string) {
      date_string = new Date(0).toDateString();
    }
    if (date_string.indexOf('T') > -1) {
      date = date_string.split('T')[0];
    } else if (Number.isInteger(Number(date_string))) {
      date = Number(date_string);
    }
    return new Date(date).getTime();
  };
  if(!b.duration?.start_date){
    return -1;
  } else if(!a.duration?.start_date){
    return 1;
  }
  // Sort by most recent date at top.
  return getTime(b.duration.start_date) - getTime(a.duration.start_date);
};

export const getBarRadius = ({
  hasLeft,
  hasRight
}: {
  hasLeft: boolean;
  hasRight: boolean;
}): [number, number, number, number] => {
  if (!hasLeft && !hasRight) return FULLY_ROUNDED;
  if (hasLeft && hasRight) return [0, 0, 0, 0];
  if (hasLeft) return RIGHT_ROUNDED;
  return LEFT_ROUNDED;
};